import React, { useState } from "react";
import { Box, Tooltip, IconButton } from "@mui/material";
import WorkspaceSplitView from "./WorkspaceSplitView";
import UndoIcon from "@mui/icons-material/Undo";
import ContentPasteIcon from "@mui/icons-material/ContentPaste";
import CopyButton from "../shared/CopyButton";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { useTasksTab } from "../../hooks/Tasks/useTasksTab";
import {
  STATUS_CONFIG,
  TASK_TYPE_TARGET_MAP,
  GROUP_KEY_TO_TASK_TYPE,
  TARGET_TYPE_LABELS,
  PRIMARY_COLOR,
} from "./constants";
import {
  Target,
  TargetStatus,
  Task,
  TargetType,
  SimpleTargetValue,
  PersonTargetValue,
  FieldResourceTargetValue,
  FieldResourceEntry,
} from "./types";
import { formatRelativeTime } from "../shared/helpers/formatRelativeTime";
import { tokens } from "../../theme/tokens";

type FieldResourceTab = "investigator" | "processor";

const TYPE_SHORT_LABEL: Record<string, string> = {
  PHONE: "Phone",
  EMAIL: "Email",
  ADDRESS: "Address",
  PROFILE: "Profile",
  PERSON: "Person",
  OTHER: "Other",
  FIELD_RESOURCE: "Resource",
};

const STATUS_ORDER: TargetStatus[] = ["NEW", "DOING", "BAD", "WORKED"];

function getTargetDisplayText(target: Target): string {
  if (target.type === "FIELD_RESOURCE") return target.label;
  if (target.type === "PERSON") return (target.value as PersonTargetValue).name ?? (target.value as SimpleTargetValue).text ?? target.label ?? "";
  return (target.value as SimpleTargetValue).text ?? "";
}

function getTargetNotes(target: Target): string {
  if (target.type === "FIELD_RESOURCE") {
    const val = target.value as FieldResourceTargetValue;
    const resourceId = Object.keys(val.data ?? {})[0] ?? "";
    return val.data?.[resourceId]?.notes ?? "";
  }
  return (target.value as SimpleTargetValue).notes ?? "";
}

// ─── Status chips ─────────────────────────────────────────────────────────────
export const StatusChips: React.FC<{ target: Target; taskId: string }> = ({
  target,
  taskId,
}) => {
  const { updateTargetStatus } = useTasksTab();
  return (
    <Box sx={{ display: "flex", gap: "4px", flexShrink: 0 }}>
      {STATUS_ORDER.map((status) => {
        const cfg = STATUS_CONFIG[status];
        const isActive = target.status === status;
        return (
          <button
            key={status}
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              if (!isActive)
                updateTargetStatus(taskId, target.target_id, status);
            }}
            style={{
              fontSize: "11px",
              fontWeight: isActive ? 600 : 400,
              color: isActive ? cfg.color : tokens.color.text.secondary,
              backgroundColor: isActive ? cfg.bg : "transparent",
              border: `1px solid ${isActive ? cfg.border : tokens.color.border.default}`,
              borderRadius: "20px",
              padding: "3px 10px",
              cursor: isActive ? "default" : "pointer",
              transition: "all 0.12s ease",
              whiteSpace: "nowrap",
            }}
          >
            {cfg.label}
          </button>
        );
      })}
    </Box>
  );
};

// ─── Type selector (simple targets only) ─────────────────────────────────────
const TypeSelector: React.FC<{ target: Target; task: Task }> = ({
  target,
  task,
}) => {
  const { updateTargetType } = useTasksTab();

  const taskType =
    task.task_type ?? GROUP_KEY_TO_TASK_TYPE[task.group_key] ?? "CUSTOM";
  const allowedTypes = TASK_TYPE_TARGET_MAP[taskType].filter(
    (t) => t !== "FIELD_RESOURCE",
  );

  if (allowedTypes.length <= 1) return null;

  return (
    <select
      value={target.type}
      onChange={(e) =>
        updateTargetType(
          task.task_id,
          target.target_id,
          e.target.value as TargetType,
        )
      }
      style={{
        fontSize: "10px",
        color: tokens.color.text.secondary,
        backgroundColor: tokens.color.surface.sunken,
        border: `1px solid ${tokens.color.border.default}`,
        borderRadius: "5px",
        padding: "2px 4px",
        cursor: "pointer",
        outline: "none",
        flexShrink: 0,
      }}
    >
      {allowedTypes.map((t) => (
        <option key={t} value={t}>
          {TARGET_TYPE_LABELS[t]}
        </option>
      ))}
    </select>
  );
};

// ─── Target row (list view) ───────────────────────────────────────────────────
const TargetRow: React.FC<{
  target: Target;
  task: Task;
  onOpen: (targetId: string) => void;
}> = ({ target, task, onOpen }) => {
  const { markAsWinning } = useTasksTab();
  const isWinner = task.primary_target_ids.includes(target.target_id);
  const hasNotes = !!getTargetNotes(target);

  return (
    <Box
      onClick={() => onOpen(target.target_id)}
      sx={{
        py: "10px",
        borderBottom: `1px solid ${tokens.color.border.default}`,
        cursor: "pointer",
        borderRadius: "6px",
        px: "6px",
        mx: "-6px",
        transition: "background-color 0.1s ease",
        "&:hover": { backgroundColor: tokens.color.surface.sunken },
        "&:last-child": { borderBottom: "none" },
        "& .copy-btn": { opacity: 0, transition: "opacity 0.15s" },
        "&:hover .copy-btn": { opacity: 1 },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
        <Tooltip
          title={isWinner ? "Winning Lead" : "Set as Winning Lead"}
          placement="top"
        >
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              markAsWinning(task.task_id, target.target_id);
            }}
            sx={{
              p: "2px",
              color: isWinner ? tokens.color.workflow.doing.fg : `${tokens.color.border.strong}`,
              "&:hover": { color: tokens.color.workflow.doing.fg },
              flexShrink: 0,
            }}
          >
            {isWinner ? (
              <StarIcon sx={{ fontSize: "14px" }} />
            ) : (
              <StarBorderIcon sx={{ fontSize: "14px" }} />
            )}
          </IconButton>
        </Tooltip>

        <span
          style={{
            fontSize: "10px",
            fontWeight: 600,
            color: tokens.color.text.secondary,
            backgroundColor: tokens.color.surface.subtle,
            border: `1px solid ${tokens.color.border.default}`,
            borderRadius: "4px",
            padding: "1px 5px",
            letterSpacing: "0.04em",
            textTransform: "uppercase",
            flexShrink: 0,
          }}
        >
          {TYPE_SHORT_LABEL[target.type] ?? target.type}
        </span>

        <span
          style={{
            fontSize: "13px",
            color: tokens.color.text.primary,
            flex: 1,
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            fontWeight: isWinner ? 500 : 400,
          }}
        >
          {getTargetDisplayText(target)}
        </span>

        {hasNotes && (
          <Box
            sx={{
              width: "6px",
              height: "6px",
              borderRadius: "50%",
              backgroundColor: tokens.color.text.secondary,
              flexShrink: 0,
            }}
          />
        )}

        <span
          style={{
            fontSize: "10px",
            color: tokens.color.border.strong,
            flexShrink: 0,
            marginRight: "4px",
          }}
        >
          {formatRelativeTime(target.updated_at)}
        </span>

        <StatusChips target={target} taskId={task.task_id} />
        <Box className="copy-btn">
          <CopyButton value={getTargetDisplayText(target)} />
        </Box>
      </Box>
    </Box>
  );
};

// ─── Shared label components ──────────────────────────────────────────────────
const FieldLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span
    style={{
      fontSize: "10px",
      fontWeight: 700,
      color: tokens.color.text.secondary,
      textTransform: "uppercase",
      letterSpacing: "0.05em",
      display: "block",
      marginBottom: "4px",
    }}
  >
    {children}
  </span>
);

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <span
    style={{
      fontSize: "10px",
      fontWeight: 700,
      color: tokens.color.text.secondary,
      textTransform: "uppercase",
      letterSpacing: "0.06em",
      display: "block",
      marginBottom: "8px",
    }}
  >
    {children}
  </span>
);

// ─── Field resource detail form ───────────────────────────────────────────────
const FieldResourceDetailEditor: React.FC<{
  target: Target;
  taskId: string;
}> = ({ target, taskId }) => {
  const { updateFieldResourceData } = useTasksTab();

  const value = target.value as FieldResourceTargetValue;
  const resourceId = Object.keys(value.data ?? {})[0] ?? "";
  const saved = (value.data ?? {})[resourceId] ?? {};

  const [form, setForm] = useState<FieldResourceEntry>({
    rush_fee: saved.rush_fee ?? null,
    locate_fee: saved.locate_fee ?? null,
    date_hired_mailed: saved.date_hired_mailed ?? null,
    date_completed: saved.date_completed ?? null,
    amount_hours_authorized: saved.amount_hours_authorized ?? null,
    mileage_billed: saved.mileage_billed ?? null,
    invoice_received: saved.invoice_received ?? null,
    tracking_number: saved.tracking_number ?? null,
    rural_property_fee: saved.rural_property_fee ?? null,
    date_last_attempt: saved.date_last_attempt ?? null,
    affidavit_completed: saved.affidavit_completed ?? null,
    amount_billed: saved.amount_billed ?? null,
    database_report_billed: saved.database_report_billed ?? null,
    invoice_paid_sent_to_ap: saved.invoice_paid_sent_to_ap ?? null,
    notes: saved.notes ?? null,
  });

  React.useEffect(() => {
    const v = target.value as FieldResourceTargetValue;
    const rId = Object.keys(v.data ?? {})[0] ?? "";
    const s = (v.data ?? {})[rId] ?? {};
    setForm({
      rush_fee: s.rush_fee ?? null,
      locate_fee: s.locate_fee ?? null,
      date_hired_mailed: s.date_hired_mailed ?? null,
      date_completed: s.date_completed ?? null,
      amount_hours_authorized: s.amount_hours_authorized ?? null,
      mileage_billed: s.mileage_billed ?? null,
      invoice_received: s.invoice_received ?? null,
      tracking_number: s.tracking_number ?? null,
      rural_property_fee: s.rural_property_fee ?? null,
      date_last_attempt: s.date_last_attempt ?? null,
      affidavit_completed: s.affidavit_completed ?? null,
      amount_billed: s.amount_billed ?? null,
      database_report_billed: s.database_report_billed ?? null,
      invoice_paid_sent_to_ap: s.invoice_paid_sent_to_ap ?? null,
      notes: s.notes ?? null,
    });
  }, [target.updated_at]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveEntry = (updated: FieldResourceEntry) => {
    updateFieldResourceData(taskId, target.target_id, resourceId, updated);
  };

  const handleBlur = <K extends keyof FieldResourceEntry>(
    key: K,
    val: FieldResourceEntry[K],
  ) => {
    const updated = { ...form, [key]: val };
    setForm(updated);
    saveEntry(updated);
  };

  const handleBoolToggle = (
    key: keyof FieldResourceEntry,
    val: boolean | null,
  ) => {
    const updated = { ...form, [key]: val };
    setForm(updated);
    saveEntry(updated);
  };

  const inputSx: React.CSSProperties = {
    fontSize: "12px",
    padding: "5px 8px",
    border: `1px solid ${tokens.color.border.strong}`,
    borderRadius: "5px",
    outline: "none",
    color: tokens.color.text.primary,
    backgroundColor: tokens.color.surface.sunken,
    width: "100%",
    boxSizing: "border-box",
    height: "30px",
    fontFamily: "inherit",
  };

  const BoolToggle: React.FC<{ fieldKey: keyof FieldResourceEntry }> = ({
    fieldKey,
  }) => {
    const cur = form[fieldKey] as boolean | null | undefined;
    return (
      <Box sx={{ display: "flex", gap: "4px" }}>
        {([true, false] as const).map((opt) => {
          const isActive = cur === opt;
          return (
            <button
              key={String(opt)}
              type="button"
              onClick={() => handleBoolToggle(fieldKey, isActive ? null : opt)}
              style={{
                fontSize: "11px",
                fontWeight: isActive ? 600 : 400,
                color: isActive ? tokens.color.surface.default : tokens.color.text.secondary,
                backgroundColor: isActive
                  ? opt
                    ? tokens.color.workflow.worked.fg
                    : tokens.color.status.error
                  : tokens.color.surface.subtle,
                border: `1px solid ${isActive ? (opt ? tokens.color.workflow.worked.fg : tokens.color.status.error) : tokens.color.border.default}`,
                borderRadius: "5px",
                padding: "4px 10px",
                cursor: "pointer",
                transition: "all 0.12s ease",
              }}
            >
              {opt ? "Yes" : "No"}
            </button>
          );
        })}
      </Box>
    );
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "14px" }}>
      <Box
        sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}
      >
        <Box>
          <FieldLabel>Rush Fee</FieldLabel>
          <BoolToggle fieldKey="rush_fee" />
        </Box>
        <Box>
          <FieldLabel>Locate Fee</FieldLabel>
          <BoolToggle fieldKey="locate_fee" />
        </Box>
        <Box>
          <FieldLabel>Rural Property Fee</FieldLabel>
          <BoolToggle fieldKey="rural_property_fee" />
        </Box>
        <Box>
          <FieldLabel>Tracking Number</FieldLabel>
          <input
            type="text"
            value={form.tracking_number ?? ""}
            onChange={(e) =>
              setForm((f) => ({
                ...f,
                tracking_number: e.target.value || null,
              }))
            }
            onBlur={(e) =>
              handleBlur("tracking_number", e.target.value || null)
            }
            style={inputSx}
            placeholder="—"
          />
        </Box>
        <Box>
          <FieldLabel>Date Hired / Mailed</FieldLabel>
          <input
            type="date"
            value={form.date_hired_mailed ?? ""}
            onChange={(e) =>
              setForm((f) => ({
                ...f,
                date_hired_mailed: e.target.value || null,
              }))
            }
            onBlur={(e) =>
              handleBlur("date_hired_mailed", e.target.value || null)
            }
            style={inputSx}
          />
        </Box>
        <Box>
          <FieldLabel>Date Completed</FieldLabel>
          <input
            type="date"
            value={form.date_completed ?? ""}
            onChange={(e) =>
              setForm((f) => ({ ...f, date_completed: e.target.value || null }))
            }
            onBlur={(e) => handleBlur("date_completed", e.target.value || null)}
            style={inputSx}
          />
        </Box>
        <Box>
          <FieldLabel>Date Last Attempt</FieldLabel>
          <input
            type="date"
            value={form.date_last_attempt ?? ""}
            onChange={(e) =>
              setForm((f) => ({
                ...f,
                date_last_attempt: e.target.value || null,
              }))
            }
            onBlur={(e) =>
              handleBlur("date_last_attempt", e.target.value || null)
            }
            style={inputSx}
          />
        </Box>
        <Box>
          <FieldLabel>Affidavit Completed</FieldLabel>
          <input
            type="date"
            value={form.affidavit_completed ?? ""}
            onChange={(e) =>
              setForm((f) => ({
                ...f,
                affidavit_completed: e.target.value || null,
              }))
            }
            onBlur={(e) =>
              handleBlur("affidavit_completed", e.target.value || null)
            }
            style={inputSx}
          />
        </Box>
        <Box>
          <FieldLabel>Amount / Hours Auth ($)</FieldLabel>
          <input
            type="number"
            value={form.amount_hours_authorized ?? ""}
            onChange={(e) =>
              setForm((f) => ({
                ...f,
                amount_hours_authorized:
                  e.target.value === "" ? null : Number(e.target.value),
              }))
            }
            onBlur={(e) =>
              handleBlur(
                "amount_hours_authorized",
                e.target.value === "" ? null : Number(e.target.value),
              )
            }
            style={inputSx}
            placeholder="—"
            min="0"
            step="0.01"
          />
        </Box>
        <Box>
          <FieldLabel>Mileage Billed (#)</FieldLabel>
          <input
            type="number"
            value={form.mileage_billed ?? ""}
            onChange={(e) =>
              setForm((f) => ({
                ...f,
                mileage_billed:
                  e.target.value === "" ? null : Number(e.target.value),
              }))
            }
            onBlur={(e) =>
              handleBlur(
                "mileage_billed",
                e.target.value === "" ? null : Number(e.target.value),
              )
            }
            style={inputSx}
            placeholder="—"
            min="0"
            step="0.01"
          />
        </Box>
        <Box>
          <FieldLabel>Invoice Received ($)</FieldLabel>
          <input
            type="number"
            value={form.invoice_received ?? ""}
            onChange={(e) =>
              setForm((f) => ({
                ...f,
                invoice_received:
                  e.target.value === "" ? null : Number(e.target.value),
              }))
            }
            onBlur={(e) =>
              handleBlur(
                "invoice_received",
                e.target.value === "" ? null : Number(e.target.value),
              )
            }
            style={inputSx}
            placeholder="—"
            min="0"
            step="0.01"
          />
        </Box>
        <Box>
          <FieldLabel>Amount Billed ($)</FieldLabel>
          <input
            type="number"
            value={form.amount_billed ?? ""}
            onChange={(e) =>
              setForm((f) => ({
                ...f,
                amount_billed:
                  e.target.value === "" ? null : Number(e.target.value),
              }))
            }
            onBlur={(e) =>
              handleBlur(
                "amount_billed",
                e.target.value === "" ? null : Number(e.target.value),
              )
            }
            style={inputSx}
            placeholder="—"
            min="0"
            step="0.01"
          />
        </Box>
        <Box>
          <FieldLabel>Database Report Billed (#)</FieldLabel>
          <input
            type="number"
            value={form.database_report_billed ?? ""}
            onChange={(e) =>
              setForm((f) => ({
                ...f,
                database_report_billed:
                  e.target.value === "" ? null : Number(e.target.value),
              }))
            }
            onBlur={(e) =>
              handleBlur(
                "database_report_billed",
                e.target.value === "" ? null : Number(e.target.value),
              )
            }
            style={inputSx}
            placeholder="—"
            min="0"
          />
        </Box>
        <Box>
          <FieldLabel>Invoice Paid / Sent to AP ($)</FieldLabel>
          <input
            type="number"
            value={form.invoice_paid_sent_to_ap ?? ""}
            onChange={(e) =>
              setForm((f) => ({
                ...f,
                invoice_paid_sent_to_ap:
                  e.target.value === "" ? null : Number(e.target.value),
              }))
            }
            onBlur={(e) =>
              handleBlur(
                "invoice_paid_sent_to_ap",
                e.target.value === "" ? null : Number(e.target.value),
              )
            }
            style={inputSx}
            placeholder="—"
            min="0"
            step="0.01"
          />
        </Box>
      </Box>

      {/* Notes — full width */}
      <Box>
        <FieldLabel>Notes</FieldLabel>
        <textarea
          value={form.notes ?? ""}
          onChange={(e) =>
            setForm((f) => ({ ...f, notes: e.target.value || null }))
          }
          onBlur={(e) => handleBlur("notes", e.target.value || null)}
          placeholder="Add notes…"
          rows={4}
          style={{
            width: "100%",
            fontSize: "12px",
            color: tokens.color.text.primary,
            backgroundColor: tokens.color.surface.sunken,
            border: `1px solid ${tokens.color.border.strong}`,
            borderRadius: "5px",
            padding: "8px 10px",
            outline: "none",
            resize: "vertical",
            fontFamily: "inherit",
            lineHeight: "1.5",
            boxSizing: "border-box",
          }}
        />
      </Box>
    </Box>
  );
};

// ─── Target detail view ───────────────────────────────────────────────────────
export const TargetDetailView: React.FC<{
  target: Target;
  task: Task;
  onBack: () => void;
}> = ({ target, task, onBack }) => {
  const { updateTargetNotes, updateTargetText } = useTasksTab();
  const isWinner = task.primary_target_ids.includes(target.target_id);
  const isFieldResource = target.type === "FIELD_RESOURCE";

  const displayText = getTargetDisplayText(target);
  const [notesText, setNotesText] = useState(getTargetNotes(target));
  const [titleEditing, setTitleEditing] = useState(
    !displayText && !isFieldResource,
  );
  const [titleText, setTitleText] = useState(displayText);

  React.useEffect(() => {
    setNotesText(getTargetNotes(target));
    setTitleText(getTargetDisplayText(target));
  }, [target.updated_at]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleNotesBlur = () => {
    updateTargetNotes(task.task_id, target.target_id, notesText);
  };

  const commitTitle = () => {
    const t = titleText.trim();
    if (t) updateTargetText(task.task_id, target.target_id, t);
    setTitleEditing(false);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
      }}
    >
      {/* Detail header */}
      <Box
        sx={{
          px: "20px",
          pt: "14px",
          pb: "14px",
          borderBottom: `1px solid ${tokens.color.border.default}`,
          flexShrink: 0,
        }}
      >
        <Box
          sx={{ display: "flex", alignItems: "center", gap: "8px", mb: "10px" }}
        >
          <IconButton
            size="small"
            onClick={onBack}
            sx={{
              p: "4px",
              color: tokens.color.text.secondary,
              border: `1px solid ${tokens.color.border.default}`,
              borderRadius: "6px",
              "&:hover": { backgroundColor: tokens.color.surface.subtle },
            }}
          >
            <CloseIcon sx={{ fontSize: "14px" }} />
          </IconButton>
          <span style={{ fontSize: "11px", color: tokens.color.text.secondary }}>Close</span>
        </Box>

        <Box sx={{ display: "flex", alignItems: "flex-start", gap: "8px" }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {/* Title — click to edit for simple targets */}
            {!isFieldResource && titleEditing ? (
              <input
                autoFocus
                value={titleText}
                onChange={(e) => setTitleText(e.target.value)}
                onBlur={commitTitle}
                onKeyDown={(e) => {
                  if (e.key === "Enter") commitTitle();
                  if (e.key === "Escape") {
                    setTitleText(displayText);
                    setTitleEditing(false);
                  }
                }}
                placeholder="Add a title…"
                style={{
                  fontSize: "16px",
                  fontWeight: 600,
                  color: tokens.color.text.primary,
                  border: "none",
                  borderBottom: `2px solid ${PRIMARY_COLOR}`,
                  outline: "none",
                  background: "transparent",
                  width: "100%",
                  padding: "0 0 2px 0",
                }}
              />
            ) : (
              <span
                onClick={() => {
                  if (!isFieldResource) setTitleEditing(true);
                }}
                style={{
                  fontSize: "16px",
                  fontWeight: 600,
                  color: displayText ? tokens.color.text.primary : `${tokens.color.text.secondary}`,
                  wordBreak: "break-word",
                  cursor: isFieldResource ? "default" : "text",
                  display: "block",
                }}
              >
                {titleText || "Add a title…"}
              </span>
            )}
            {isWinner && (
              <Box sx={{ mt: "4px" }}>
                <span
                  style={{
                    fontSize: "10px",
                    fontWeight: 700,
                    color: tokens.color.workflow.worked.fg,
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                  }}
                >
                  ★ Winning Lead
                </span>
              </Box>
            )}
          </Box>
        </Box>

        <Box
          sx={{
            mt: "10px",
            display: "flex",
            alignItems: "center",
            gap: "10px",
            flexWrap: "wrap",
          }}
        >
          <StatusChips target={target} taskId={task.task_id} />
          {!isFieldResource && <TypeSelector target={target} task={task} />}
        </Box>
      </Box>

      {/* Scrollable body */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          px: "20px",
          py: "18px",
          display: "flex",
          flexDirection: "column",
          gap: "22px",
        }}
      >
        {/* Field resource form (includes notes) */}
        {isFieldResource && (
          <FieldResourceDetailEditor target={target} taskId={task.task_id} />
        )}

        {/* Notes (non-field-resource targets only) */}
        {!isFieldResource && (
          <Box>
            <SectionLabel>Notes</SectionLabel>
            <textarea
              value={notesText}
              onChange={(e) => setNotesText(e.target.value)}
              onBlur={handleNotesBlur}
              placeholder="Add notes about this objective…"
              rows={5}
              style={{
                width: "100%",
                fontSize: "13px",
                color: tokens.color.text.primary,
                backgroundColor: tokens.color.surface.sunken,
                border: `1px solid ${tokens.color.border.default}`,
                borderRadius: "7px",
                padding: "10px 12px",
                outline: "none",
                resize: "vertical",
                fontFamily: "inherit",
                lineHeight: "1.5",
                boxSizing: "border-box",
              }}
              onFocus={(e) => {
                e.target.style.borderColor = PRIMARY_COLOR;
              }}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

// ─── Workspace header ─────────────────────────────────────────────────────────
const WorkspaceHeader: React.FC<{ task: Task }> = ({ task }) => {
  const { openPasteModal, undo, undoSnapshot } = useTasksTab();
  return (
    <Box
      sx={{
        px: "20px",
        pt: "18px",
        pb: "14px",
        borderBottom: `1px solid ${tokens.color.border.default}`,
        flexShrink: 0,
      }}
    >
      <span
        style={{
          fontSize: "10px",
          fontWeight: 700,
          color: tokens.color.text.secondary,
          textTransform: "uppercase",
          letterSpacing: "0.08em",
        }}
      >
        Workspace
      </span>
      <Box
        sx={{ display: "flex", alignItems: "center", gap: "10px", mt: "5px" }}
      >
        <span
          style={{
            fontSize: "20px",
            fontWeight: 700,
            color: tokens.color.text.primary,
            flex: 1,
            minWidth: 0,
          }}
        >
          {task.title}
        </span>
        <Box
          sx={{
            display: "flex",
            gap: "6px",
            flexShrink: 0,
            alignItems: "center",
          }}
        >
          {undoSnapshot && (
            <Tooltip title="Undo last status change" placement="top">
              <button
                type="button"
                onClick={undo}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  fontSize: "11px",
                  fontWeight: 600,
                  color: tokens.color.text.primary,
                  backgroundColor: tokens.color.surface.subtle,
                  border: `1px solid ${tokens.color.border.default}`,
                  borderRadius: "6px",
                  padding: "5px 10px",
                  cursor: "pointer",
                }}
              >
                <UndoIcon style={{ fontSize: "13px" }} />
                Undo
              </button>
            </Tooltip>
          )}
          <button
            type="button"
            onClick={() => openPasteModal(task.task_id)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "4px",
              fontSize: "11px",
              fontWeight: 600,
              color: tokens.color.text.primary,
              backgroundColor: tokens.color.surface.subtle,
              border: `1px solid ${tokens.color.border.default}`,
              borderRadius: "6px",
              padding: "5px 10px",
              cursor: "pointer",
            }}
          >
            <ContentPasteIcon style={{ fontSize: "13px" }} />
            Add Objectives
          </button>
        </Box>
      </Box>
    </Box>
  );
};

// ─── Workspace panel ──────────────────────────────────────────────────────────
const FILTER_ORDER: TargetStatus[] = ["NEW", "DOING", "WORKED", "BAD"];

const WorkspacePanel: React.FC = () => {
  const { selectedTask, fieldResources } = useTasksTab();
  const [selectedTargetId, setSelectedTargetId] = useState<string | null>(null);
  const [fieldResourceTab, setFieldResourceTab] =
    useState<FieldResourceTab>("investigator");
  const [filterStatus, setFilterStatus] = useState<TargetStatus | "ALL">("ALL");
  const [winnerIndex, setWinnerIndex] = useState(0);

  const isFieldResourceTask = selectedTask?.group_key === "FIELD_RESOURCES";

  // Clear detail view, reset tab, clear status filter, and reset winner index when the task changes
  React.useEffect(() => {
    setSelectedTargetId(null);
    setFieldResourceTab("investigator");
    setFilterStatus("ALL");
    setWinnerIndex(0);
  }, [selectedTask?.task_id]);

  if (!selectedTask) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          alignItems: "center",
          justifyContent: "center",
          gap: "10px",
          color: tokens.color.border.strong,
          userSelect: "none",
        }}
      >
        <TaskAltOutlinedIcon sx={{ fontSize: "36px", opacity: 0.4 }} />
        <span style={{ fontSize: "13px" }}>
          Select a task to open workspace
        </span>
      </Box>
    );
  }

  // ── Split view when a target is selected ──
  if (selectedTargetId) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          overflow: "hidden",
        }}
      >
        <WorkspaceHeader task={selectedTask} />

        {/* Split view body */}
        <Box sx={{ flex: 1, overflow: "hidden" }}>
          <WorkspaceSplitView
            task={selectedTask}
            selectedTargetId={selectedTargetId}
            onSelectTarget={setSelectedTargetId}
            onClose={() => setSelectedTargetId(null)}
          />
        </Box>
      </Box>
    );
  }

  // ── List view (no target selected) ──
  const winningTargets = selectedTask.targets.filter((t) =>
    selectedTask.primary_target_ids.includes(t.target_id),
  );
  const clampedWinnerIndex = Math.min(
    winnerIndex,
    Math.max(0, winningTargets.length - 1),
  );
  const activeWinner = winningTargets[clampedWinnerIndex];

  // For FIELD_RESOURCES tasks, filter targets by the active tab type
  const tabFilteredTargets = isFieldResourceTask
    ? selectedTask.targets.filter((t) => {
        if (t.type !== "FIELD_RESOURCE") return false;
        const resourceId = Object.keys(
          (t.value as FieldResourceTargetValue).data ?? {},
        )[0];
        const resource = fieldResources.find((r) => r.id === resourceId);
        return (resource?.type ?? "investigator") === fieldResourceTab;
      })
    : selectedTask.targets;

  const visibleTargets =
    filterStatus === "ALL"
      ? tabFilteredTargets
      : tabFilteredTargets.filter((t) => t.status === filterStatus);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
      }}
    >
      <WorkspaceHeader task={selectedTask} />

      {/* Scrollable body */}
      <Box sx={{ flex: 1, overflowY: "auto", px: "20px", py: "18px" }}>
        {/* Winning Lead card — single card with carousel nav if multiple winners */}
        {activeWinner && (
          <Box
            sx={{
              mb: "22px",
              borderLeft: `4px solid ${tokens.color.workflow.worked.fg}`,
              pl: "14px",
              pr: "14px",
              py: "12px",
              backgroundColor: tokens.color.workflow.worked.bg,
              borderRadius: "0 8px 8px 0",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: "5px",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <span
                  style={{
                    fontSize: "10px",
                    fontWeight: 700,
                    color: tokens.color.workflow.worked.fg,
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                  }}
                >
                  ★ Winning Lead
                </span>
                <span
                  style={{
                    fontSize: "10px",
                    fontWeight: 600,
                    color: tokens.color.text.primary,
                    backgroundColor: tokens.color.surface.default,
                    border: `1px solid ${tokens.color.border.strong}`,
                    borderRadius: "4px",
                    padding: "1px 5px",
                    letterSpacing: "0.04em",
                    textTransform: "uppercase",
                  }}
                >
                  {TYPE_SHORT_LABEL[activeWinner.type] ?? activeWinner.type}
                </span>
              </Box>
              {winningTargets.length > 1 && (
                <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                  <button
                    type="button"
                    onClick={() => setWinnerIndex((i) => Math.max(0, i - 1))}
                    disabled={clampedWinnerIndex === 0}
                    style={{
                      fontSize: "12px",
                      color: clampedWinnerIndex === 0 ? `${tokens.color.border.strong}` : tokens.color.workflow.worked.fg,
                      background: "none",
                      border: "none",
                      cursor: clampedWinnerIndex === 0 ? "default" : "pointer",
                      padding: "0 2px",
                      lineHeight: 1,
                    }}
                  >
                    ‹
                  </button>
                  <span
                    style={{
                      fontSize: "10px",
                      color: tokens.color.text.secondary,
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {clampedWinnerIndex + 1} / {winningTargets.length}
                  </span>
                  <button
                    type="button"
                    onClick={() =>
                      setWinnerIndex((i) =>
                        Math.min(winningTargets.length - 1, i + 1),
                      )
                    }
                    disabled={clampedWinnerIndex === winningTargets.length - 1}
                    style={{
                      fontSize: "12px",
                      color:
                        clampedWinnerIndex === winningTargets.length - 1
                          ? tokens.color.border.strong
                          : tokens.color.workflow.worked.fg,
                      background: "none",
                      border: "none",
                      cursor:
                        clampedWinnerIndex === winningTargets.length - 1
                          ? "default"
                          : "pointer",
                      padding: "0 2px",
                      lineHeight: 1,
                    }}
                  >
                    ›
                  </button>
                </Box>
              )}
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: "6px",
                mb: "12px",
                "& .copy-btn": { opacity: 0, transition: "opacity 0.15s" },
                "&:hover .copy-btn": { opacity: 1 },
              }}
            >
              <span
                style={{
                  fontSize: "16px",
                  fontWeight: 600,
                  color: tokens.color.text.primary,
                  flex: 1,
                }}
              >
                {getTargetDisplayText(activeWinner)}
              </span>
              <Box className="copy-btn">
                <CopyButton value={getTargetDisplayText(activeWinner)} />
              </Box>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                flexWrap: "wrap",
              }}
            >
              <StatusChips
                target={activeWinner}
                taskId={selectedTask.task_id}
              />
              <span
                style={{ fontSize: "10px", color: tokens.color.text.secondary, flexShrink: 0 }}
              >
                {formatRelativeTime(activeWinner.updated_at)}
              </span>
            </Box>
          </Box>
        )}

        {/* Targets section */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: "8px",
          }}
        >
          <span
            style={{
              fontSize: "10px",
              fontWeight: 700,
              color: tokens.color.text.secondary,
              textTransform: "uppercase",
              letterSpacing: "0.08em",
            }}
          >
            Objectives — click to view details
          </span>
          {isFieldResourceTask && (
            <Box sx={{ display: "flex", gap: "4px" }}>
              {(["investigator", "processor"] as FieldResourceTab[]).map(
                (tab) => {
                  const isActive = fieldResourceTab === tab;
                  return (
                    <button
                      key={tab}
                      type="button"
                      onClick={() => setFieldResourceTab(tab)}
                      style={{
                        fontSize: "11px",
                        fontWeight: isActive ? 600 : 400,
                        color: isActive ? tokens.color.surface.default : tokens.color.text.secondary,
                        backgroundColor: isActive ? PRIMARY_COLOR : tokens.color.surface.subtle,
                        border: isActive
                          ? `1px solid ${PRIMARY_COLOR}`
                          : `1px solid ${tokens.color.border.default}`,
                        borderRadius: "20px",
                        padding: "3px 10px",
                        cursor: "pointer",
                        transition: "all 0.12s ease",
                      }}
                    >
                      {tab.charAt(0).toUpperCase() + tab.slice(1)}
                    </button>
                  );
                },
              )}
            </Box>
          )}
        </Box>

        {/* Status filter pills */}
        <Box sx={{ display: "flex", gap: "4px", flexWrap: "wrap", mb: "12px" }}>
          <button
            type="button"
            onClick={() => setFilterStatus("ALL")}
            style={{
              display: "flex",
              alignItems: "baseline",
              gap: "5px",
              fontSize: "11px",
              fontWeight: filterStatus === "ALL" ? 600 : 400,
              color: filterStatus === "ALL" ? tokens.color.surface.default : tokens.color.text.secondary,
              backgroundColor:
                filterStatus === "ALL" ? PRIMARY_COLOR : tokens.color.surface.subtle,
              border:
                filterStatus === "ALL"
                  ? `1px solid ${PRIMARY_COLOR}`
                  : `1px solid ${tokens.color.border.default}`,
              borderRadius: "20px",
              padding: "3px 10px",
              cursor: "pointer",
              transition:
                "background-color 0.12s ease, color 0.12s ease, border-color 0.12s ease",
            }}
          >
            All
            <span style={{ fontSize: "10px", opacity: 0.75 }}>
              {tabFilteredTargets.length}
            </span>
          </button>
          {FILTER_ORDER.map((status) => {
            const cfg = STATUS_CONFIG[status];
            const isActive = filterStatus === status;
            const count = tabFilteredTargets.filter(
              (t) => t.status === status,
            ).length;
            return (
              <button
                key={status}
                type="button"
                onClick={() => setFilterStatus(isActive ? "ALL" : status)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "5px",
                  fontSize: "11px",
                  fontWeight: isActive ? 600 : 400,
                  color: isActive ? cfg.color : tokens.color.text.secondary,
                  backgroundColor: isActive ? cfg.bg : tokens.color.surface.subtle,
                  border: `1px solid ${isActive ? cfg.border : tokens.color.border.default}`,
                  borderRadius: "20px",
                  padding: "3px 10px",
                  cursor: "pointer",
                  transition:
                    "background-color 0.12s ease, color 0.12s ease, border-color 0.12s ease",
                }}
              >
                {cfg.label}
                <span style={{ fontSize: "10px", opacity: 0.75 }}>{count}</span>
              </button>
            );
          })}
        </Box>

        {visibleTargets.length === 0 ? (
          <Box sx={{ py: "28px", textAlign: "center", color: tokens.color.border.strong }}>
            <span style={{ fontSize: "13px" }}>
              {selectedTask.targets.length === 0
                ? "No objectives yet — paste some to get started"
                : tabFilteredTargets.length === 0
                  ? `No ${fieldResourceTab}s assigned`
                  : `No ${STATUS_CONFIG[filterStatus as TargetStatus].label} objectives`}
            </span>
          </Box>
        ) : (
          <Box sx={{ mt: "8px" }}>
            {visibleTargets.map((target) => (
              <TargetRow
                key={target.target_id}
                target={target}
                task={selectedTask}
                onOpen={setSelectedTargetId}
              />
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default WorkspacePanel;
